import React, { useState, useEffect, useRef } from 'react';
import { FiPlus, FiTrash2, FiEdit2, FiGrid, FiMapPin, FiChevronRight, FiArrowLeft, FiCalendar, FiUser, FiLayers, FiDownload } from 'react-icons/fi';
import api from '../../utils/api';

const SIGNAL_URL = window.location.hostname === 'localhost'
    ? 'http://localhost:5001'
    : 'https://erp.learninghub.ind.in:5001';

const font = "'Outfit', sans-serif";
const C = { text: '#0f172a', sub: '#64748b', border: '#bae6fd', bg: '#f0f9ff', white: '#fff', accent: '#0ea5e9', success: '#059669', danger: '#ef4444' };

const ManageHalls = () => {
    // Data states
    const [halls, setHalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [msg, setMsg] = useState('');
    const [search, setSearch] = useState('');

    // Navigation states
    const [view, setView] = useState('blocks'); // blocks | halls | detail
    const [selectedBlock, setSelectedBlock] = useState(null);
    const [selectedHall, setSelectedHall] = useState(null);
    const [selectedSession, setSelectedSession] = useState(null); 
    const [selectedSeatDetail, setSelectedSeatDetail] = useState(null);

    // Detail data
    const [hallSchedules, setHallSchedules] = useState([]);
    const [layoutSeats, setLayoutSeats] = useState([]);

    // Form state
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ hall_number: '', capacity: '', building: '' });
    const [editingId, setEditingId] = useState(null);


    // Live proctoring stats from signaling server (real-time during active exam)
    const [liveStats, setLiveStats] = useState(null); // { headTurns, tabSwitches, online }
    const liveStatsPollRef = useRef(null);

    const fetchLiveStats = async (username) => {
        if (!username) return;
        try {
            const SIGNAL_HTTP = SIGNAL_URL.replace('wss://', 'https://').replace('ws://', 'http://');
            const res = await fetch(`${SIGNAL_HTTP}/live-stats/${encodeURIComponent(username)}`);
            if (res.ok) {
                const data = await res.json();
                setLiveStats(data);
            }
        } catch {
            setLiveStats(null);
        }
    };

    // When a seat card is clicked, start polling for live stats
    const openStudentCard = (seat) => {
        setSelectedSeatDetail(seat);
        setLiveStats(null);
        if (liveStatsPollRef.current) clearInterval(liveStatsPollRef.current);
        if (seat.student_username) {
            fetchLiveStats(seat.student_username);
            liveStatsPollRef.current = setInterval(() => fetchLiveStats(seat.student_username), 5000);
        }
    };

    const closeStudentCard = () => {
        setSelectedSeatDetail(null);
        setLiveStats(null);
        if (liveStatsPollRef.current) { clearInterval(liveStatsPollRef.current); liveStatsPollRef.current = null; }
    };


    const fetchHalls = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/exam-controller/halls');
            setHalls(res.data.halls || []);
        } catch (e) { setError('Failed to load halls'); }
        finally { setLoading(false); }
    };

    // ── PDF Export ───────────────────────────────────────────────────────────
    const downloadPDF = async () => {
        const { jsPDF } = await import('jspdf');
        // autoTable must be called as a standalone fn when dynamically imported
        const autoTable = (await import('jspdf-autotable')).default;

        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const hall = selectedHall;
        const session = selectedSession;
        const seats = layoutSeats;

        // ── Page header ─────────────────────────────────────────────────────
        doc.setFillColor(14, 165, 233); // accent blue
        doc.rect(0, 0, 210, 38, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('SVU Exam Portal — Hall Seating Plan', 14, 14);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Hall: ${hall?.hall_number}   |   Block: ${hall?.building || 'N/A'}   |   Capacity: ${hall?.capacity}`, 14, 22);
        if (session) {
            doc.text(`Subject: ${session.subject_name} (${session.subject_code || ''})`, 14, 29);
            doc.text(`Date: ${session.exam_date?.slice(0, 10) || 'N/A'}   |   Time: ${session.exam_time || 'N/A'}`, 14, 35);
        }
        doc.setTextColor(0, 0, 0);

        // ── Build table rows ─────────────────────────────────────────────────
        const hallNo   = hall?.hall_number || '—';
        const hallName = hall?.building    || '—';
        const occupied = seats.filter(s => s.student_name || s.student_username);
        const tableBody = seats.map(seat => [
            hallNo,
            hallName,
            seat.seat_number,
            seat.roll_number || seat.student_username || '—',
            seat.student_name || '—',
            seat.dept || seat.batch_name || '—',
        ]);

        autoTable(doc, {
            startY: 44,
            head: [['Hall No.', 'Block / Name', 'Seat No.', 'Roll Number', 'Student Name', 'Dept / Batch']],
            body: tableBody,
            theme: 'grid',
            headStyles: {
                fillColor: [14, 165, 233],
                textColor: 255,
                fontStyle: 'bold',
                fontSize: 8.5,
            },
            bodyStyles: { fontSize: 8.5, cellPadding: 2.5 },
            alternateRowStyles: { fillColor: [240, 249, 255] },
            columnStyles: {
                0: { halign: 'center', cellWidth: 20, fontStyle: 'bold', textColor: [14, 165, 233] },
                1: { cellWidth: 30 },
                2: { halign: 'center', cellWidth: 18 },
                3: { cellWidth: 34 },
                4: { cellWidth: 52 },
                5: { cellWidth: 36 },
            },
            didParseCell: (data) => {
                // Grey out empty-seat rows (student name is dash)
                if (data.section === 'body' && data.row.raw[4] === '—') {
                    data.cell.styles.textColor = [148, 163, 184];
                }
            },
            margin: { top: 44, left: 10, right: 10 },
        });

        // ── Footer summary ───────────────────────────────────────────────────
        const finalY = (doc.lastAutoTable?.finalY || 44) + 8;
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text(
            `Total Seats: ${seats.length}   |   Occupied: ${occupied.length}   |   Empty: ${seats.length - occupied.length}   |   Generated: ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}`,
            14, finalY
        );

        // ── Save ─────────────────────────────────────────────────────────────
        const filename = `Hall_${hall?.hall_number}_${session?.exam_date?.slice(0, 10) || 'layout'}.pdf`;
        doc.save(filename);
    };

    useEffect(() => { fetchHalls(); }, []);

    const filteredHalls = halls.filter(h => {
        const q = search.toLowerCase();
        return !search || 
            h.hall_number?.toString().toLowerCase().includes(q) || 
            h.building?.toLowerCase().includes(q);
    });

    const blocks = Array.from(new Set(filteredHalls.map(h => h.building || 'General Block')));

    const openBlock = (b) => {
        setSelectedBlock(b);
        setView('halls');
    };

    const openHall = async (h) => {
        setSelectedHall(h);
        setView('detail');
        setSelectedSession(null);
        setSelectedSeatDetail(null);
        setError(null);
        try {
            const res = await api.get(`/api/exam-controller/halls/${h.id}/schedules`);
            const schedules = res.data.schedules || [];
            setHallSchedules(schedules);
            
            // Auto-load the first/most recent session layout so it's not "blank"
            if (schedules.length > 0) {
                // Pass 'h' explicitly because setSelectedHall is async
                viewSessionLayout(schedules[0], h);
            } else {
                const blank = Array.from({ length: h.capacity }).map((_, i) => ({ seat_number: i + 1, student_name: null }));
                setLayoutSeats(blank);
            }
        } catch (e) { setError('Failed to load hall data'); }
    };

    const viewSessionLayout = async (session, explicitHall = null) => {
        const hall = explicitHall || selectedHall;
        if (!hall) return;

        setSelectedSession(session);
        setSelectedSeatDetail(null);
        try {
            const res = await api.get(`/api/exam-controller/assignments/${session.assignment_id}/seat-plan`);
            const sessionSeats = res.data.seats || [];
            
            const fullLayout = Array.from({ length: hall.capacity }).map((_, i) => {
                const num = i + 1;
                const match = sessionSeats.find(s => s.seat_number === num);
                return match || { seat_number: num, student_name: null };
            });
            setLayoutSeats(fullLayout);
        } catch (e) { setError('Failed to load session layout'); }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (editingId) await api.put(`/api/exam-controller/halls/${editingId}`, form);
            else await api.post('/api/exam-controller/halls', form);
            setForm({ hall_number: '', capacity: '', building: '' });
            setEditingId(null); setShowForm(false);
            fetchHalls();
        } catch (err) { setError('Failed to save hall'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this hall?')) return;
        try {
            await api.delete(`/api/exam-controller/halls/${id}`);
            fetchHalls();
            if (selectedHall?.id === id) {
                 setView('blocks');
                 setSelectedHall(null);
            }
        } catch (err) { setError('Failed to delete hall'); }
    };

    const renderBreadcrumbs = () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.sub }}>
            <span onClick={() => { setView('blocks'); setSelectedBlock(null); setSelectedHall(null); setSelectedSession(null); setSearch(''); }} style={{ cursor: 'pointer', fontWeight: view === 'blocks' ? 800 : 500, color: view === 'blocks' ? C.accent : C.sub }}>All Blocks</span>
            {selectedBlock && (
                <>
                    <FiChevronRight size={14} />
                    <span onClick={() => { setView('halls'); setSelectedHall(null); setSelectedSession(null); setSearch(''); }} style={{ cursor: 'pointer', fontWeight: view === 'halls' ? 800 : 500, color: view === 'halls' ? C.accent : C.sub }}>{selectedBlock}</span>
                </>
            )}
            {selectedHall && (
                <>
                    <FiChevronRight size={14} />
                    <span onClick={() => { setView('detail'); setSelectedSession(null); }} style={{ cursor: 'pointer', fontWeight: view === 'detail' && !selectedSession ? 800 : 500, color: view === 'detail' && !selectedSession ? C.accent : C.sub }}>Hall {selectedHall.hall_number}</span>
                </>
            )}
            {selectedSession && (
                <>
                    <FiChevronRight size={14} />
                    <span style={{ fontWeight: 800, color: C.accent }}>{selectedSession.subject_name.slice(0, 20)}...</span>
                </>
            )}
        </div>
    );

    const renderStudentDetailModal = () => {
        if (!selectedSeatDetail) return null;
        const s = selectedSeatDetail;
        // Use live stats (from signaling server) when student is in active exam; fallback to DB values
        const headTurns = (liveStats && liveStats.online) ? liveStats.headTurns : (s.head_turn_count ?? 0);
        const tabSwitches = (liveStats && liveStats.online) ? liveStats.tabSwitches : (s.tab_switch_count ?? 0);
        const isOnline = liveStats?.online || false;
        return (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={closeStudentCard}>
                <div style={{ background: '#fff', padding: 32, borderRadius: 28, border: `2px solid ${C.accent}`, width: '100%', maxWidth: 450, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.3)', position: 'relative' }} onClick={e => e.stopPropagation()}>
                    <button onClick={closeStudentCard} style={{ position: 'absolute', top: 20, right: 20, border: 'none', background: C.bg, color: C.accent, borderRadius: '50%', width: 34, height: 34, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 16 }}>✕</button>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 30 }}>
                        <div style={{ width: 64, height: 64, borderRadius: 20, background: '#e0f2fe', color: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <FiUser size={32} />
                        </div>
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 850, color: C.accent, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>Seat {s.seat_number} — Examinee</div>
                            <h2 style={{ fontSize: 24, fontWeight: 900, color: C.text, margin: 0, lineHeight: 1.1 }}>{s.student_name}</h2>
                            <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: isOnline ? '#22c55e' : '#94a3b8', display: 'inline-block', boxShadow: isOnline ? '0 0 6px #22c55e' : 'none' }} />
                                <span style={{ fontSize: 11, fontWeight: 700, color: isOnline ? '#16a34a' : '#94a3b8' }}>{isOnline ? '🟢 Currently in Exam' : '⚪ Not in exam'}</span>
                            </div>
                        </div>
                    </div>
                    
                    <div style={{ display: 'grid', gap: 12 }}>
                        {/* Primary Credentials */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div style={{ padding: '12px 16px', background: C.bg, borderRadius: 16, border: `1.5px solid ${C.border}` }}>
                                <div style={{ fontSize: 10, fontWeight: 800, color: C.sub, marginBottom: 3, textTransform: 'uppercase' }}>Roll Number</div>
                                <div style={{ fontSize: 13, fontWeight: 900, color: C.text }}>{s.roll_number}</div>
                            </div>
                            <div style={{ padding: '12px 16px', background: C.bg, borderRadius: 16, border: `1.5px solid ${C.border}` }}>
                                <div style={{ fontSize: 10, fontWeight: 800, color: C.sub, marginBottom: 3, textTransform: 'uppercase' }}>Username</div>
                                <div style={{ fontSize: 13, fontWeight: 900, color: C.text }}>{s.student_username}</div>
                            </div>
                        </div>

                        {/* Security ID */}
                        {s.anonymous_id && (
                            <div style={{ padding: '12px 16px', background: 'linear-gradient(135deg, #0ea5e9, #0369a1)', borderRadius: 16, color: '#fff', boxShadow: '0 4px 12px rgba(14,165,233,0.2)' }}>
                                <div style={{ fontSize: 10, fontWeight: 800, opacity: 0.9, marginBottom: 3, textTransform: 'uppercase' }}>Security Anon ID</div>
                                <div style={{ fontSize: 14, fontWeight: 900 }}>{s.anonymous_id}</div>
                            </div>
                        )}

                        <div style={{ height: 1, background: '#f1f5f9', margin: '10px 0' }} />

                        {/* Academic Details */}
                        <div style={{ display: 'grid', gap: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                <span style={{ color: C.sub, fontWeight: 700 }}>Department:</span>
                                <span style={{ color: C.text, fontWeight: 800 }}>{s.dept || 'N/A'}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                <span style={{ color: C.sub, fontWeight: 700 }}>Batch:</span>
                                <span style={{ color: C.text, fontWeight: 800 }}>{s.batch_name || 'N/A'}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                <span style={{ color: C.sub, fontWeight: 700 }}>Studying Year:</span>
                                <span style={{ color: C.text, fontWeight: 800 }}>{s.studying_year || 'N/A'}</span>
                            </div>
                        </div>

                        <div style={{ height: 1, background: '#f1f5f9', margin: '10px 0' }} />

                        {/* Contact info */}
                        <div style={{ display: 'grid', gap: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{ width: 30, height: 30, borderRadius: 8, background: '#f0fdf4', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📞</div>
                                <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>{s.phone_number || 'No Phone Registered'}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{ width: 30, height: 30, borderRadius: 8, background: '#fff7ed', color: '#ea580c', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✉️</div>
                                <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>{s.email || 'No Email Registered'}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'start', gap: 12 }}>
                                <div style={{ width: 30, height: 30, borderRadius: 8, background: '#faf5ff', color: '#9333ea', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>📍</div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: C.sub, lineHeight: 1.4 }}>{s.address || 'Address not provided in system.'}</div>
                            </div>
                        </div>
                                          {/* ── Proctoring & Live Monitor Section — ALWAYS VISIBLE ── */}
                        <div style={{ height: 1, background: '#f1f5f9', margin: '10px 0' }} />
                        <div>
                            <div style={{ fontSize: 10, fontWeight: 900, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                                🔍 Proctoring Stats
                                {isOnline && <span style={{ background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 800 }}>LIVE • Auto-refreshes</span>}
                            </div>

                            {/* Counts – from live signaling stats when online, DB otherwise */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginBottom: 10 }}>
                                <div style={{ textAlign: 'center', padding: '12px 8px', background: (tabSwitches > 0) ? '#fef2f2' : '#f0fdf4', borderRadius: 14, border: `2px solid ${(tabSwitches > 0) ? '#fca5a5' : '#86efac'}` }}>
                                    <div style={{ fontSize: 32, fontWeight: 900, color: (tabSwitches > 0) ? '#dc2626' : '#16a34a', lineHeight: 1 }}>{tabSwitches}</div>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#78350f', marginTop: 4 }}>Tab Switches</div>
                                </div>
                            </div>

                            {/* Submission Status */}
                            <div style={{ marginTop: 10 }}>
                                {s.submitted_at != null ? (
                                    <div style={{ padding: '10px 14px', borderRadius: 12, background: s.is_auto_submitted ? '#fef2f2' : '#f0fdf4', border: `1.5px solid ${s.is_auto_submitted ? '#fca5a5' : '#86efac'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontSize: 12, fontWeight: 800, color: s.is_auto_submitted ? '#dc2626' : '#16a34a' }}>{s.is_auto_submitted ? '🚨 AUTO SUBMITTED' : '✅ Submitted'}</span>
                                        {s.total_score != null && <span style={{ fontSize: 15, fontWeight: 900, color: '#4338ca' }}>Score: {s.total_score}</span>}
                                    </div>
                                ) : (
                                    <div style={{ padding: '10px 14px', borderRadius: 12, background: '#fffbeb', border: '1.5px solid #fcd34d', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#92400e' }}>⏳ Exam in progress — not yet submitted</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <button onClick={closeStudentCard} style={{ width: '100%', marginTop: 24, padding: '14px', borderRadius: 16, border: 'none', background: C.text, color: '#fff', fontWeight: 900, fontSize: 14, cursor: 'pointer', transition: 'all 0.2s' }} onMouseEnter={e=>{e.currentTarget.style.opacity=0.9; e.currentTarget.style.transform='scale(0.98)'}} onMouseLeave={e=>{e.currentTarget.style.opacity=1; e.currentTarget.style.transform='scale(1)'}}>
                        Close Profile
                    </button>
                </div>
            </div>
        );
    };

    if (loading && halls.length === 0) return <div style={{ padding: 100, textAlign: 'center', fontFamily: font }}>Loading Infrastructure...</div>;

    return (
        <div style={{ fontFamily: font, maxWidth: 1300, margin: '0 auto', position: 'relative' }}>

            <div style={{ marginBottom: 30, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <FiLayers size={22} color={C.accent} />
                        <h1 style={{ fontSize: 24, fontWeight: 900, color: C.text, margin: 0 }}>Hall Infrastructure</h1>
                    </div>
                    {renderBreadcrumbs()}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 15, flexWrap: 'wrap' }}>
                    <div style={{ position: 'relative', width: '100%', maxWidth: 280 }}>
                        <input 
                            type="text" 
                            placeholder={view === 'blocks' ? "🔍 Search blocks..." : "🔍 Search halls..."} 
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ width: '100%', padding: '10px 15px', borderRadius: 12, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: font, outline: 'none', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
                        />
                        {search && (
                            <button 
                                onClick={() => setSearch('')} 
                                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: C.sub, cursor: 'pointer', fontSize: 16, fontWeight: 800 }}
                            >
                                ×
                            </button>
                        )}
                    </div>
                    <button onClick={() => { setEditingId(null); setForm({ hall_number: '', capacity: '', building: selectedBlock || '' }); setShowForm(!showForm); }} style={{ padding: '10px 20px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#38bdf8,#0ea5e9)', color: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 12px rgba(14,165,233,0.3)' }}>
                        {showForm ? 'Cancel' : <><FiPlus size={16} /> New Hall</>}
                    </button>
                </div>
            </div>

            {renderStudentDetailModal()}

            {error && <div style={{ padding: 14, background: '#fef2f2', color: C.danger, borderRadius: 14, marginBottom: 20, fontSize: 13, fontWeight: 700, border: '1.5px solid #fca5a5' }}>{error}</div>}

            {showForm && (
                <div style={{ background: C.white, borderRadius: 24, border: `2px solid ${C.border}`, padding: 24, marginBottom: 30, boxShadow: '0 15px 35px rgba(0,0,0,0.1)' }}>
                    <form onSubmit={handleSave} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20, alignItems: 'end' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: C.sub, textTransform: 'uppercase', marginBottom: 6 }}>Block Name</label>
                            <input value={form.building} onChange={e => setForm({ ...form, building: e.target.value })} required placeholder="e.g. Science Block" style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: font }} />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: C.sub, textTransform: 'uppercase', marginBottom: 6 }}>Hall Number</label>
                            <input value={form.hall_number} onChange={e => setForm({ ...form, hall_number: e.target.value })} required placeholder="e.g. 101" style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: font }} />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: C.sub, textTransform: 'uppercase', marginBottom: 6 }}>Capacity</label>
                            <input type="number" value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} required placeholder="50" style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: font }} />
                        </div>
                        <button type="submit" style={{ padding: '10px', borderRadius: 10, border: 'none', background: C.accent, color: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
                            {editingId ? 'Update Hall' : 'Create Hall'}
                        </button>
                    </form>
                </div>
            )}

            {view === 'blocks' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 24 }}>
                    {blocks.map(b => {
                        const count = halls.filter(h => (h.building || 'General Block') === b).length;
                        return (
                            <div key={b} onClick={() => openBlock(b)} style={{ background: C.white, borderRadius: 24, border: `2px solid ${C.border}`, padding: 30, cursor: 'pointer', transition: 'all 0.2s', position: 'relative', overflow: 'hidden' }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.transform = 'translateY(-5px)'; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.transform = 'translateY(0)'; }}
                            >
                                <div style={{ width: 50, height: 50, borderRadius: 16, background: C.bg, color: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                                    <FiGrid size={24} />
                                </div>
                                <h3 style={{ fontSize: 18, fontWeight: 900, color: C.text, margin: '0 0 4px' }}>{b}</h3>
                                <p style={{ fontSize: 13, color: C.sub, margin: 0 }}>{count} {count === 1 ? 'Hall' : 'Halls'}</p>
                                <FiChevronRight style={{ position: 'absolute', right: 24, bottom: 24, color: C.border }} size={24} />
                            </div>
                        );
                    })}
                </div>
            )}

            {view === 'halls' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 20 }}>
                    <div onClick={() => { setView('blocks'); setSelectedBlock(null); setSearch(''); }} style={{ background: C.white, borderRadius: 20, border: `1.5px solid ${C.border}`, padding: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, color: C.sub }} onMouseEnter={e => e.currentTarget.style.color = C.text}>
                        <FiArrowLeft size={18} /> <span style={{ fontWeight: 700, fontSize: 13 }}>Back</span>
                    </div>
                    {filteredHalls.filter(h => (h.building || 'General Block') === selectedBlock).map(h => (
                        <div key={h.id} onClick={() => openHall(h)} style={{ background: C.white, borderRadius: 20, border: `1.5px solid ${C.border}`, padding: 20, cursor: 'pointer', transition: 'all 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.background = '#f8fafc'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = '#fff'; }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 16 }}>
                                <div style={{ padding: '6px 12px', background: C.accent, color: '#fff', borderRadius: 10, fontSize: 15, fontWeight: 900 }}>{h.hall_number}</div>
                                <div style={{ display: 'flex', gap: 5 }}>
                                    <button onClick={(e) => { e.stopPropagation(); setEditingId(h.id); setForm({ ...h }); setShowForm(true); }} style={{ padding: 6, borderRadius: 8, border: 'none', background: C.bg, color: C.accent }}><FiEdit2 size={12} /></button>
                                    <button onClick={(e) => { e.stopPropagation(); handleDelete(h.id); }} style={{ padding: 6, borderRadius: 8, border: 'none', background: '#fee2e2', color: C.danger }}><FiTrash2 size={12} /></button>
                                </div>
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: C.sub }}>Capacity: <span style={{ color: C.text }}>{h.capacity}</span></div>
                        </div>
                    ))}
                </div>
            )}

            {view === 'detail' && selectedHall && (
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 350px', gap: 30 }}>
                    <div>
                        <div style={{ background: C.white, borderRadius: 24, border: `2px solid ${C.border}`, overflow: 'hidden', minHeight: 500, display: 'flex', flexDirection: 'column' }}>
                            <div style={{ padding: '18px 24px', background: C.bg, borderBottom: `2px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                                <div>
                                    <h2 style={{ fontSize: 17, fontWeight: 900, color: C.text, margin: 0 }}>🪑 Seating Layout — {selectedHall.hall_number}</h2>
                                    <p style={{ fontSize: 12, color: C.sub, margin: 0 }}>{selectedHall.building} • {selectedHall.capacity} Seats</p>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    {selectedSession && (
                                        <div style={{ background: '#ecfdf5', color: C.success, padding: '6px 14px', borderRadius: 12, fontSize: 11, fontWeight: 800, border: '1px solid #6ee7b7', maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {selectedSession.subject_name}
                                        </div>
                                    )}
                                    <button
                                        onClick={downloadPDF}
                                        title="Download seating plan as PDF"
                                        style={{ padding: '7px 14px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#38bdf8,#0ea5e9)', color: '#fff', fontWeight: 800, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 3px 8px rgba(14,165,233,0.3)' }}
                                    >
                                        <FiDownload size={14} /> Export PDF
                                    </button>
                                </div>
                            </div>
                            <div style={{ padding: 40, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 15, flex: 1, overflowY: 'auto' }}>
                                {layoutSeats.map(s => {
                                    const isOccupied = s.student_name || s.student_username;
                                    return (
                                        <div key={s.seat_number} 
                                onClick={() => isOccupied && openStudentCard(s)}
                                            style={{ padding: '14px', borderRadius: 16, border: `1.5px solid ${isOccupied ? '#bbf7d0' : '#e2e8f0'}`, background: isOccupied ? '#f0fdf4' : '#fff', textAlign: 'center', transition: 'all 0.2s', cursor: isOccupied ? 'pointer' : 'default' }}
                                            onMouseEnter={e => { if (isOccupied) e.currentTarget.style.transform = 'translateY(-3px)'; }}
                                            onMouseLeave={e => { if (isOccupied) e.currentTarget.style.transform = 'translateY(0)'; }}
                                        >
                                            <div style={{ fontSize: 9, fontWeight: 900, color: isOccupied ? C.success : C.sub, marginBottom: 4 }}>SEAT {s.seat_number}</div>
                                            <div style={{ fontSize: 11, fontWeight: 800, color: isOccupied ? C.text : '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {isOccupied ? s.student_name : 'Empty'}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div style={{ background: C.white, borderRadius: 24, border: `2px solid ${C.border}`, padding: 24, height: 'fit-content' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                            <div style={{ width: 34, height: 34, borderRadius: 10, background: '#e0f2fe', color: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiCalendar /></div>
                            <h3 style={{ fontSize: 16, fontWeight: 900, margin: 0 }}>Hall Schedule</h3>
                        </div>
                        {hallSchedules.length === 0 ? (
                            <div style={{ padding: 40, textAlign: 'center', color: C.sub, background: C.bg, borderRadius: 16, fontSize: 13 }}>No sessions yet.</div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {hallSchedules.map(sch => {
                                    const isAct = selectedSession?.assignment_id === sch.assignment_id;
                                    return (
                                        <div key={sch.assignment_id} onClick={() => viewSessionLayout(sch)} style={{ padding: '16px', borderRadius: 18, border: `2px solid ${isAct ? C.accent : 'transparent'}`, background: isAct ? '#e0f2fe' : C.bg, cursor: 'pointer', transition: 'all 0.2s' }}>
                                            <div style={{ fontSize: 13, fontWeight: 900, color: C.text, marginBottom: 6 }}>{sch.subject_name}</div>
                                            <div style={{ fontSize: 11, color: C.sub, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                                                <FiCalendar size={12} /> {sch.exam_date?.slice(0, 10)} • {sch.exam_time}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, background: '#fff', padding: '6px 10px', borderRadius: 10, border: '1px solid #e0f2fe', color: C.sub, fontWeight: 700 }}>
                                                <FiUser /> {sch.invigilator_names || sch.invigilator_username || 'No Invigilator'}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageHalls;
